import { FC, useContext, useEffect, useLayoutEffect, useReducer, useState } from "react";
import AuthSubHeader from "src/components/authSubHeader";
import { LoaderContext } from "src/context/loaderContext";
import { EOBs_TABLE_HEADER } from "src/constants/cases";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import ReactPaginate from "react-paginate";
import { EOBsAPIResponse, QueryParamsState, QueryStateActions, QueryStateActionType } from "./types";
import ExplanationOfBenefitFilters from "src/components/EOBsFilter";
import { initialState, queryParamReducer } from "./reducer";
import { AuthContext } from "src/context/authContext";
import { format } from "date-fns";
import AuthMaunfacturerSubHeader from "src/components/manufacturer/authManufacturerSubHeader";
import ViewPdf from "src/components/viewPdf";
// import { FooterLogo } from "src/assets";
import FooterText from "src/components/footerText";
import EoBNoteModal from "src/components/modals/eobNoteModal";
import { checkCookie } from "src/helpers";



const EOBsListing: FC = (): JSX.Element => {
  const [queryParams, dispatch] = useReducer(queryParamReducer, initialState);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { isManufacturer } = useContext(AuthContext);
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [selectedPdfId, setSelectedPdfId] = useState<number>();
  const [Queries, setUpdatedQueries] = useState<QueryParamsState>(queryParams);
  const [showNoteModal, updateNoteModalVisibility] = useState<boolean>(false)

  useLayoutEffect(() => {
    setLoader();

    if (!checkCookie("eob-visited")) {
      updateNoteModalVisibility(true)
      const year = 1000*60*60*24*365;
      const expires = new Date((new Date()).valueOf() + year);
      document.cookie = "eob-visited=true;expires=" + expires.toUTCString();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { error: eobsError, data: eobsListing, mutate: getEOBsListing, removeErrorAndData: clearEOBsListingState } =
    useFetch<EOBsAPIResponse>(APIEndpoints.EOBs_LISTING, queryParams);

  useEffect(() => {
    if (eobsListing || eobsError)
      hideLoader();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eobsListing, eobsError])

  const handleSearchWithQueryParam = (updatedState?: QueryParamsState) => {
    updatedState && setUpdatedQueries(updatedState)
    if (eobsError || eobsListing) clearEOBsListingState();
    setLoader();
    getEOBsListing(null, null, updatedState ? updatedState : queryParams);
  };

  const onClearButtonClicked = () => {
    dispatch({
      type: QueryStateActions.CLEAR_STATE,
    });
    setUpdatedQueries(queryParams)
    getEOBsListing(null, null, initialState);
  };

  const changeRecordOrderByColumn = (value: string): void => {
    const order = Queries?.direction === 'desc' ? 'asc' : 'desc';
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_SORT_PARAMS,
      payload: {
        direction: queryParams?.sort_by === value ? order : "desc",
        sort_by: value,
      },
    };
    dispatch(action);
    if (Queries) {
      const updatedState = queryParamReducer(Queries, action);
      handleSearchWithQueryParam(updatedState);
    }
  };

  const changePageNumber = (page: { selected: number }): void => {
    Queries.page = page.selected + 1
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_PAGE,
      payload: page.selected + 1,
    };
    dispatch(action);
    if (Queries) {
      const updatedState = queryParamReducer(Queries, action);
      handleSearchWithQueryParam(updatedState);
    }
  };

  const updateQueryParamsAndCallAPI = (
    updatedQueries: QueryParamsState,
    isReset: boolean = false
  ): void => {

    setLoader();
    handleSearchWithQueryParam(updatedQueries)
  };


  return (
    <div className="container-fluid cases-page overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
      {isManufacturer ? <AuthMaunfacturerSubHeader /> : <AuthSubHeader />}
      <div className="card m-1 mt-4 flex-fill">
        <div className="card-header bg-success-subtle row g-0 justify-content-around align-items-center ">
          <h3 className="text-black m-0">
            EOB Warehouse
          </h3>
        </div>
        <div className="card-body">
          <p>
            {`The following is a list of de-identifiable EOBs which may be downloaded. To filter this list, simply select one or more values from the dropdowns below and click 'Find'.`}<br />
            {/* {`Please note, reimbursement amounts may vary based on each individual provider's contract with the payer.`} */}
          </p>
          <ExplanationOfBenefitFilters
            state={queryParams}
            dispatch={dispatch}
            defaultQueryParams={queryParams}
            handleSearchWithQueryParam={handleSearchWithQueryParam}
            updateQueryParams={updateQueryParamsAndCallAPI}
            onClearButtonClicked={onClearButtonClicked}
          />

          <div className="table-responsive-sm table-responsive-md table-responsive-lg">
            <table className="table table-striped table-sm mt-3">
              <thead>
                <tr className="text-center">
                  {EOBs_TABLE_HEADER.map((tableHeader, index) => (
                    <th
                      key={index} 
                      className={`bg-primary ${tableHeader.value === "" ? "text-black" : "text-white"} fs-6`}
                      onClick={tableHeader.value !== '' ? () => changeRecordOrderByColumn(tableHeader.value) : undefined}
                    >
                      <span
                        role={tableHeader.value === '' ? "textbox" : "button"}
                        className={tableHeader.value === '' ?
                          '' :
                          'btn text-white btn-link link-underline link-underline-opacity-0 p-0 fw-bold'
                        }
                      >
                        {tableHeader.label}
                        {queryParams.sort_by === tableHeader.value ? (
                          <i className={`bi ${queryParams.direction === 'desc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}></i>
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {eobsListing && eobsListing.data && eobsListing.data.length > 0 ?
                  [...eobsListing.data]
                    .sort((a, b) => (a.insurance_name || '').localeCompare(b.insurance_name || ''))
                    .map((record) => (
                      <tr key={record.id} className="align-middle text-center">
                        <td className="fs-6">{record ? record.insurance_name : ''}</td>
                        <td className="fs-6">{record ? record.insurance_type : ''}</td>
                        <td className="fs-6">{record ? record.insurance_state : ''}</td>
                        <td className="fs-6">{record ? record.insurance_group : ''}</td>
                        <td className="fs-6">{record ? record.speciality : ''}</td>
                        <td className="fs-6">{record ? record.insurance_mac : ''}</td>
                        <td className="fs-6">{record ? record.cpt_codes : ''}</td>
                        <td className="fs-6">{record && record.service_date ? format(new Date(record.service_date), "MM/dd/yyyy") : ''}</td>
                        <td className="fs-6">
                          {record && record.redacted_file &&
                            <button
                              type="button"
                              className="btn btn-outline-secondary rounded-0"
                              onClick={() => {
                                setSelectedPdfId(record.id);
                                setShowPdf(true);
                              }} >
                              <i className="fas fa-file-download"></i> Download</button>
                          }
                        </td>
                      </tr>
                    )) : null
                }
              </tbody>
            </table>
          </div>

          {eobsListing && eobsListing.total_pages ? (
            <ul className="pagination justify-content-center flex-wrap">
              {queryParams.page !== 1 ? (
                <li className="page-item">
                  <button
                    className="btn btn-link page-link"
                    onClick={() => changePageNumber({ selected: 0 })}
                  >
                    <i className="bi bi-chevron-double-left"></i> First
                  </button>
                </li>
              ) : null}

              <li>
                <ReactPaginate
                  nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
                  onPageChange={changePageNumber}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={eobsListing.total_pages}
                  previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakLabel="..."
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination"
                  activeClassName="active"
                  renderOnZeroPageCount={null}
                  forcePage={queryParams.page - 1}
                />
              </li>

              {queryParams.page !== eobsListing.total_pages ? (
                <li className={`page-item ${queryParams.page === eobsListing.total_pages ? 'disabled' : ''}`}>
                  <button
                    className="btn btn-link page-link rounded-start-0"
                    onClick={() => changePageNumber({ selected: eobsListing.total_pages - 1 })}
                  >
                    Last <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}
          <p> IMPORTANT: By utilizing resources provided here (e.g., explanation of benefit (EOB) documents), you acknowledge that provided materials are informational only, do not imply or guarantee coverage or appropriateness for any health or prescription drug benefit or other outcomes, and are not intended to replace or substitute for your professional medical judgment, diagnosis, treatment, or coding decisions. You are solely responsible for your decisions and actions regarding medical care and reimbursement and for determining the accuracy and appropriateness of any diagnostic, clinical, or medical information you record or submit. Averitas Pharma, Inc. makes no representations or warranties and expressly disclaims any and all liability concerning any treatment, reimbursement submission, action by, or effect on any person based on information provided herein. Reliance on information herein is solely at your own risk, and you agree not to make any claims or statements that conflict with any of the foregoing.</p>

        </div>

      </div>

      <FooterText />

      {showNoteModal ? (
        <EoBNoteModal
          isModalVisible={showNoteModal} hideModal={() => updateNoteModalVisibility(false)}
        />
      ) : null}

      {showPdf && selectedPdfId && (
        <ViewPdf id={selectedPdfId} onClose={() => {
          setShowPdf(false);
          setSelectedPdfId(0);
        }} />
      )}
    </div>
  )
}
export default EOBsListing;
